import { AppsInsightSeverityLevel } from "@/common/models";

export const GEN_AI_SOURCE_ID = "msft_365_gen_ai";

/**
 * The six information categories used across the Gen AI Governance dashboard.
 * `icon` values map to symbols that already exist in the svg sprite.
 */
export type InfoCategoryId =
  | "personal"
  | "hr"
  | "legal"
  | "governance"
  | "business"
  | "financial";

export interface InfoCategory {
  id: InfoCategoryId;
  name: string;
  icon: string;
}

export const INFO_CATEGORIES: InfoCategory[] = [
  { id: "personal", name: "Personal", icon: "User" },
  { id: "hr", name: "HR", icon: "HR" },
  { id: "legal", name: "Legal", icon: "Legal" },
  { id: "governance", name: "Governance", icon: "Governance" },
  { id: "business", name: "Business", icon: "Business" },
  { id: "financial", name: "Financial", icon: "Finance" },
];

/**
 * A per-level split of a category's total, used to draw the stacked bars and
 * to recompute the row's headline number as the slider moves. Keyed by the
 * same strings as TONE_BY_LEVEL so one tone map drives bars, dots and chips.
 */
export interface LevelBreakdown {
  high: number;
  medium: number;
  low: number;
  none: number;
}

/**
 * The slider keys, ordered widest-last — index 0 is the "High only" stop.
 * Ami's final design gives BOTH sliders four stops ending at "All", so
 * severity includes its none bucket too.
 */
export const SEVERITY_STOPS: (keyof LevelBreakdown)[] = [
  "high",
  "medium",
  "low",
  "none",
];

export const SENSITIVITY_STOPS: (keyof LevelBreakdown)[] = [
  "high",
  "medium",
  "low",
  "none",
];

export interface CategoryCount {
  id: InfoCategoryId;
  name: string;
  icon: string;
  count: number;
  /** risk severity split — "Risk By Information Category" */
  severities?: LevelBreakdown;
  /** sensitivity split — "Information Involved in AI Activity" */
  sensitivities?: LevelBreakdown;
}

/**
 * AI file activity types — the id is the backend enum value verbatim, so these
 * map straight through once the real query is wired.
 *
 * (Ami's prototype shipped with Accessed/Modified/Created/Shared; he confirmed
 * those were wrong — the real set is the AI_* operations.)
 */
export type AiFileActivityId =
  | "created"
  | "modified"
  | "exposed"
  | "shared"
  | "uploaded";

/**
 * Which way the information moved. The two Risks cards split on this:
 * "Risky Employees' Activities" is employee -> AI (shared, uploaded) and
 * "Risky AI Agent Activities" is AI -> content (created, modified, exposed).
 * The prototype says so in its own footer copy — the two "are not the same".
 */
export type ActivityDirection = "employee" | "ai";

export interface AiFileActivity {
  id: AiFileActivityId;
  name: string;
  icon: string;
  count: number;
}

export interface AiActivityType {
  id: AiFileActivityId;
  name: string;
  icon: string;
  direction: ActivityDirection;
  /**
   * Every name this activity has been shown under, matched case-insensitively:
   * its relation code, the prototype's codes (AI_EXPOSED, SHARED_TO_AI, ...)
   * and their display strings. Only for naming a row that already carries one
   * of these (the demo rows, an event's own type) — what a card COUNTS is
   * decided by RELATION_CODE alone, see `resolve_relation`.
   */
  aliases: string[];
}

/**
 * The five activity types, in the order the prototype lists them
 * (`gen-ai-ui/metadata/activityTypes.json`, Sep 2026).
 *
 * This replaces the earlier Read/Write/Move/Share/Delete set. An older note
 * here recorded Ami rejecting a Created/Modified/Shared set in favour of the
 * AI_* operations; the prototype has since settled on this one and his latest
 * widget spec (Risky Employees' / Risky AI Agent Activities) depends on its
 * direction split, which the AI_* set cannot express.
 */
export const AI_ACTIVITIES: AiActivityType[] = [
  {
    id: "created",
    name: "Generated by AI",
    icon: "CirclePlus",
    direction: "ai",
    aliases: ["AI_CREATED", "CREATED_BY_AI", "AI Created", "AI Created Documents", "Created by AI", "Generated by AI"],
  },
  {
    id: "modified",
    name: "Modified by AI",
    icon: "Edit2",
    direction: "ai",
    aliases: ["AI_MODIFIED", "MODIFIED_BY_AI", "AI Modified", "AI Modified Documents", "Modified by AI"],
  },
  {
    id: "exposed",
    name: "Read by AI",
    icon: "Eye",
    direction: "ai",
    aliases: ["AI_EXPOSED", "EXPOSED_BY_AI", "AI Read", "AI Read Documents & Exposed User", "AI Exposed", "Exposed by AI", "Read by AI", "AI_Read"],
  },
  {
    id: "shared",
    name: "Shared to AI",
    icon: "ExposedTo",
    direction: "employee",
    aliases: ["UploadTo_AI", "SHARED_TO_AI", "PUSHED_TO_AI", "Pushed to AI", "Shared to AI", "User Shared to AI", "Shared or Uploaded to AI"],
  },
  {
    id: "uploaded",
    name: "Uploaded to AI",
    icon: "Upload",
    direction: "employee",
    aliases: ["UPLOADED_TO_AI", "User Uploaded to AI", "Uploaded to AI"],
  },
];

/**
 * The `AiContentRelation` code behind each activity. This is the one list the
 * dashboard's relations come from (frontend fix list F1, Oct 2026): the
 * queries ask for these codes, the folds count only these, and the drill-downs
 * filter on them, so a headline, its bars and its list cannot disagree.
 *
 *   employee -> AI   UploadTo_AI                        Widgets 1 and 3
 *   AI-originated    AI_Created, AI_Modified, AI_Read   Widgets 2 and 4
 *
 * "Uploaded to AI" is a row the prototype lists; the backend has no relation
 * for it, so it counts nothing.
 */
export const RELATION_CODE: Record<AiFileActivityId, string | null> = {
  created: "AI_Created",
  modified: "AI_Modified",
  exposed: "AI_Read",
  shared: "UploadTo_AI",
  uploaded: null,
};

/**
 * Relation codes that are not part of the current model. QA wrote AI_Write
 * for a while; there is no approved mapping for it, so it is neither asked
 * for nor added to "Modified by AI" — a row carrying it is reported and left
 * out. To count legacy records, map them here explicitly, once approved.
 */
export const LEGACY_RELATION_CODES = ["AI_Write"];

/** Kept for existing call sites: the same list, without the alias payload. */
export const AI_RELATIONS: { id: AiFileActivityId; name: string; icon: string }[] =
  AI_ACTIVITIES.map(({ id, name, icon }) => ({ id, name, icon }));

export const activities_for = (direction: ActivityDirection) =>
  AI_ACTIVITIES.filter((a) => a.direction === direction);

const ALIAS_INDEX: Record<string, AiFileActivityId> = AI_ACTIVITIES.reduce(
  (acc, a) => {
    [a.id, ...a.aliases].forEach((code) => {
      acc[code.toLowerCase()] = a.id;
    });
    return acc;
  },
  {} as Record<string, AiFileActivityId>
);

const DIRECTION_OF: Record<AiFileActivityId, ActivityDirection> = AI_ACTIVITIES.reduce(
  (acc, a) => ({ ...acc, [a.id]: a.direction }),
  {} as Record<AiFileActivityId, ActivityDirection>
);

/** which way an activity moves information: employee -> AI or AI -> content */
export const direction_of = (id: AiFileActivityId): ActivityDirection =>
  DIRECTION_OF[id];

/** Name a row by whatever code or display string it carries; null if unknown. */
export const resolve_activity = (code: string): AiFileActivityId | null =>
  ALIAS_INDEX[String(code || "").toLowerCase()] || null;

/**
 * The relation codes a card, a row or a list is about: one activity's code,
 * or every code of a direction. An activity with no relation in the backend
 * asks for the prototype's code for it, which matches nothing — as its 0
 * said — rather than for no relation at all, which would match everything.
 */
export const relation_codes = (
  scope: { relation?: AiFileActivityId | null; direction?: ActivityDirection } = {}
): string[] => {
  const wanted = AI_ACTIVITIES.filter((a) =>
    scope.relation ? a.id === scope.relation : !scope.direction || a.direction === scope.direction
  );
  const codes = wanted.map((a) => RELATION_CODE[a.id]).filter(Boolean) as string[];
  return codes.length ? codes : wanted.map((a) => a.aliases[0]);
};

export const UPLOAD_TO_AI = RELATION_CODE.shared as string;
/** Widgets 1 and 3 */
export const EMPLOYEE_TO_AI_CODES = relation_codes({ direction: "employee" });
/** Widgets 2 and 4 */
export const AI_ORIGINATED_CODES = relation_codes({ direction: "ai" });

const RELATION_INDEX: Record<string, AiFileActivityId> = AI_ACTIVITIES.reduce((acc, a) => {
  const code = RELATION_CODE[a.id];
  if (code) acc[code.toLowerCase()] = a.id;
  return acc;
}, {} as Record<string, AiFileActivityId>);

/**
 * The activity a counted row belongs to, by its relation code and nothing
 * else. Null for anything outside RELATION_CODE, including the legacy codes:
 * a count must not pick up a relation its query would not have asked for.
 */
export const resolve_relation = (code: string): AiFileActivityId | null =>
  RELATION_INDEX[String(code || "").toLowerCase()] || null;

export const is_legacy_relation = (code: string) =>
  LEGACY_RELATION_CODES.some((c) => c.toLowerCase() === String(code || "").toLowerCase());

/**
 * The single source both risk cards read: `w=AiContentRelation | x=Severity |
 * y=InfoTypeLabel` as one authored cube.
 *
 * Authored category-major so it can be diffed against Ami's prototype, which
 * writes it the same way. It matters that this is ONE cube: his prototype keeps
 * a separate per-category mock alongside it, and the two disagree — his cube
 * totals 261 while his category mock totals 260, so his own two cards contradict
 * each other on screen. Deriving both pivots from here makes that impossible.
 */
export type RiskCube = Record<
  InfoCategoryId,
  Record<AiFileActivityId, LevelBreakdown>
>;

/**
 * One period's denominators for the share line under each card title
 * (gen-ai-ui: "0.43% of employee shares to AI · ↓ 37% vs last month").
 */
export interface PeriodTotals {
  /** every activity of each direction, at any sensitivity (the count API) */
  activity: Record<ActivityDirection, number>;
  /**
   * every AI event of each direction, at any severity, as the events list
   * counts them. The Risks cards' pool: their risky counts come from the
   * same lists, so a share never divides one source by another.
   */
  ai_events: Record<ActivityDirection, number>;
  /** each direction's AI events at each risk severity (none stays 0) */
  risky: Record<ActivityDirection, LevelBreakdown>;
  /**
   * The Visibility cards' pool: every activity counted on content in the six
   * category families (data/categories.ts — the same L0 labels the rows'
   * drill-downs filter on), AI or not, at any sensitivity. It is a sum of
   * per-category counts, so an activity under two categories counts in both;
   * it is not a count of unique items.
   */
  events: number;
}

export interface ShareTotals {
  current: PeriodTotals;
  previous: PeriodTotals;
  /** what "vs last …" compares against: the frame's own unit */
  period: "week" | "month";
}

export const empty_period_totals = (): PeriodTotals => ({
  activity: { employee: 0, ai: 0 },
  ai_events: { employee: 0, ai: 0 },
  risky: { employee: empty_levels(), ai: empty_levels() },
  events: 0,
});

export const empty_levels = (): LevelBreakdown => ({
  high: 0,
  medium: 0,
  low: 0,
  none: 0,
});

const add_levels = (a: LevelBreakdown, b: LevelBreakdown): LevelBreakdown => ({
  high: a.high + b.high,
  medium: a.medium + b.medium,
  low: a.low + b.low,
  none: a.none + b.none,
});

export const level_total = (d: LevelBreakdown) =>
  d.high + d.medium + d.low + d.none;

/** a fully-formed zero cube, so the loading/empty state is never a hole */
export const empty_risk_cube = (): RiskCube =>
  INFO_CATEGORIES.reduce((acc, c) => {
    acc[c.id] = AI_RELATIONS.reduce((rel, r) => {
      rel[r.id] = empty_levels();
      return rel;
    }, {} as Record<AiFileActivityId, LevelBreakdown>);
    return acc;
  }, {} as RiskCube);

/**
 * W1's pivot: one row per relation. `category` narrows to a single column of
 * the cube; "all" sums across every column, which is the spec's "when no info
 * category is picked, it's the sum of all".
 */
export const by_relation = (
  cube: RiskCube,
  category: InfoCategoryId | "all",
  direction?: ActivityDirection
) =>
  (direction ? activities_for(direction) : AI_ACTIVITIES).map(({ id, name, icon }) => {
    const relation = { id, name, icon };
    const severities =
      category === "all"
        ? INFO_CATEGORIES.reduce(
            (acc, c) => add_levels(acc, cube[c.id][relation.id]),
            empty_levels()
          )
        : cube[category][relation.id];
    return { ...relation, severities, count: level_total(severities) };
  });

const LEVEL_NAME: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "None",
};

/**
 * The stacked bar in words. Colour is the only thing carrying the split on
 * screen and the per-segment tooltips are mouse-only, so without this the
 * entire content of both risk cards is unreachable by keyboard or screen
 * reader and each row announces nothing but a bare total.
 */
export const describe_levels = (
  d: LevelBreakdown | undefined,
  keys: readonly (keyof LevelBreakdown)[]
) => {
  if (!d) {
    return "";
  }
  return keys
    .filter((k) => d[k] > 0)
    .map((k) => LEVEL_NAME[k] + " " + d[k])
    .join(", ");
};

/**
 * One row per information category, summed over the relations in `direction`
 * (all of them when omitted).
 */
export const by_category = (
  cube: RiskCube,
  direction?: ActivityDirection
): CategoryCount[] =>
  INFO_CATEGORIES.map((c) => {
    const relations = direction ? activities_for(direction) : AI_ACTIVITIES;
    const severities = relations.reduce(
      (acc, r) => add_levels(acc, cube[c.id][r.id]),
      empty_levels()
    );
    return { ...c, count: level_total(severities), severities };
  });

/** One row of "Activity vs. Information Category" — an activity across categories. */
export interface ActivityMatrixRow {
  id: AiFileActivityId;
  name: string;
  icon: string;
  /** keyed by InfoCategoryId */
  counts: Record<InfoCategoryId, number>;
}

export interface AiAgent {
  id: string;
  name: string;
  icon: string;
  count: number;
}

/** One row of the "AI agents accessing sensitive information" matrix. */
export interface AiAgentMatrixRow {
  id: string;
  name: string;
  icon: string;
  /** keyed by InfoCategoryId */
  counts: Record<InfoCategoryId, number>;
  /** the category this agent is most associated with — rendered highlighted */
  highlight: InfoCategoryId;
}

export type AiEventActorType = "employee" | "agent";

export interface AiAgentEvent {
  id: string;
  by: string;
  by_desc: string;
  by_type: AiEventActorType;
  content: string;
  /** extra content items beyond the first, rendered as a "+N" chip */
  content_more: number;
  content_label: string;
  /** PII label names — render as the SpiMarker "P" badge on the content cell */
  piis: string[];
  exposed_to: string;
  exposed_to_desc: string;
  exposed_to_type: AiEventActorType;
  date: string;
  /** the AI operation this event records — what the W1 rows count */
  relation: AiFileActivityId;
  /** 1 = Low, 2 = Medium, 3 = High — matches LevelLabel */
  severity: number;
  handled: boolean;
  justification_requested: boolean;
  risk_type: string;
  info_type: InfoCategoryId;
}

/**
 * One category card in the expanded "What is your information?"-style breakdown.
 * Reused for both the information breakdown and the employee breakdown — the
 * two differ only in what `count` means (items vs employees).
 */
export interface InfoCategoryDetail {
  id: InfoCategoryId;
  name: string;
  icon: string;
  /** sub-labels listed inside the card; `category` lets a click open its own */
  items: { id: string; name: string; count: number; category?: InfoCategoryId }[];
  /** "had Microsoft Labels" percentage shown top-right of the card; null when
   *  there is no source for it, and the card then leaves it out */
  ms_coverage: number | null;
}

/** An employee row for the "# Shared By Employees" drill-down. */
export interface GenAiEmployee {
  id: string;
  name: string;
  email: string;
  department: string;
  count: number;
  category: InfoCategoryId;
}

/** A row of the "Content" tab — mirrors the shape of the app's files table. */
export interface AiAgentContentItem {
  id: string;
  name: string;
  info_type: string;
  piis: string;
  context: string;
  /** 0 = None, 1 = Low, 2 = Medium, 3 = High — matches SensitivityLabel */
  sensitivity: number;
  application: string;
  accessed_by: string;
  info_category: InfoCategoryId;
}

export interface GenAiOverview {
  content: AiAgentContentItem[];
  /** expand of "Information involved in AI activity" — counts are items */
  info_breakdown: InfoCategoryDetail[];
  /** expand of "# Shared By Employees" — counts are employees */
  employee_breakdown: InfoCategoryDetail[];
  employees: GenAiEmployee[];
  /** full agent list behind the "AI agents by activity" expand */
  agents_all: AiAgent[];
  /**
   * Category x activity x RISK severity. The two Risks cards read it, each
   * restricted to its own direction (see `ActivityDirection`).
   */
  risk_cube: RiskCube;
  /**
   * Category x activity x DATA sensitivity — a separate axis from risk. The
   * Visibility cards read it. Same shape as `risk_cube`, different meaning:
   * "none" here is unclassified content, not "no risk".
   */
  sensitivity_cube: RiskCube;
  /**
   * Distinct employees per category, for "Employees Interacted with AI about
   * Sensitive Information", split by data sensitivity in `sensitivities`.
   * Rows overlap — one person can span categories — which is why the card's
   * headline is not the sum of these.
   */
  employees_by_category: CategoryCount[];
  /** distinct employees per level across every category — the headline */
  employees_by_level: LevelBreakdown;
  /** pools for the share line under each card title */
  shares: ShareTotals;
  /** "Activity vs. Information Category" — replaced the sensitivity matrix */
  activity_matrix: ActivityMatrixRow[];
  shared_by_employees: CategoryCount[];
  ai_file_activity: AiFileActivity[];
  information_involved: CategoryCount[];
  agents_by_activity: AiAgent[];
  agents_matrix: AiAgentMatrixRow[];
  events: AiAgentEvent[];
}

export type GenAiSeverity = AppsInsightSeverityLevel;
